/**
 * Entity Registry — client-side config voor de Enterprise Entity Picker.
 *
 * Config-driven: elke entity heeft een adapter-object dat vertelt:
 *   - hoe te zoeken (welk backend endpoint via entity_type)
 *   - hoe resultaten te renderen (icon, avatar, primary/secondary/tertiary)
 *   - welke badges te tonen
 *   - waar naartoe te navigeren bij "open" actie
 *
 * Backend `/api/admin/entity-search/registry` retourneert de authoritatieve
 * lijst gefilterd op user-permissies. Deze client-side config is een
 * UI-fallback/enrichment (icons, extra visuele hints) — de source-of-truth
 * blijft de backend.
 *
 * Nieuwe entities toevoegen:
 *   1. Registreer een adapter in `/app/backend/admin_entity_search.py`
 *   2. Voeg hier één config-object toe met dezelfde `entity_type`
 *   Klaar — Entity Picker werkt automatisch.
 */
import {
  Users, FileText, Package, FileSignature, Truck,
  Ship, Anchor, Plane, Warehouse, Container as ContainerIcon,
  UserCog, Ticket, Boxes, PhoneIncoming, CalendarClock,
} from "lucide-react";

/**
 * @typedef {Object} EntityConfig
 * @property {string} entity_type         - Matcht backend adapter.entity_type
 * @property {string} label               - Label bijv "Klanten"
 * @property {React.Component} icon       - Lucide icon component
 * @property {string} placeholder         - Zoekveld-placeholder
 * @property {string} emptyLabel          - Label voor "geen resultaten"
 * @property {string} [group]             - Categorie in Command Palette
 */

/**
 * Kern-configuratie voor de meest gebruikte entities.
 * NB: Elke module kan later zijn eigen config append via
 * `registerEntityConfig(...)` (zie onder).
 */
export const ENTITY_CONFIGS = {
  customer: {
    entity_type: "customer",
    label: "Klanten",
    icon: Users,
    placeholder: "Zoek op naam, bedrijfsnaam, e-mail of telefoon…",
    emptyLabel: "Geen klant gevonden.",
    group: "Klanten",
  },
  invoice: {
    entity_type: "invoice",
    label: "Facturen",
    icon: FileText,
    placeholder: "Zoek op factuurnummer, klant of e-mail…",
    emptyLabel: "Geen factuur gevonden.",
    group: "Financieel",
  },
  booking: {
    entity_type: "booking",
    label: "Boekingen",
    icon: Package,
    placeholder: "Zoek op boekingnummer, klant of bestemming…",
    emptyLabel: "Geen boeking gevonden.",
    group: "Operatie",
  },
  quote: {
    entity_type: "quote",
    label: "Offertes",
    icon: FileSignature,
    placeholder: "Zoek op offertenummer of klant…",
    emptyLabel: "Geen offerte gevonden.",
    group: "Financieel",
  },
  shipment: {
    entity_type: "shipment",
    label: "Zendingen",
    icon: Truck,
    placeholder: "Zoek op trackingnummer of ontvanger…",
    emptyLabel: "Geen zending gevonden.",
    group: "Operatie",
  },
  pickup_delivery: {
    entity_type: "pickup_delivery",
    label: "Pickup & Delivery",
    icon: CalendarClock,
    placeholder: "Zoek op klant, plaats, telefoon, W-nummer of datum…",
    emptyLabel: "Geen afspraak gevonden.",
    group: "Operatie",
  },
  // Placeholder-configs voor toekomstige modules — backend adapters volgen
  // in vervolgsprints. Ze staan hier al zodat de Command Palette ze straks
  // automatisch oppikt zonder frontend-wijziging.
  manifest: {
    entity_type: "manifest",
    label: "Manifesten",
    icon: Ship,
    placeholder: "Zoek op manifestnummer…",
    emptyLabel: "Geen manifest gevonden.",
    group: "Operatie",
  },
  bill_of_lading: {
    entity_type: "bill_of_lading",
    label: "Bill of Lading",
    icon: Anchor,
    placeholder: "Zoek op BOL-nummer…",
    emptyLabel: "Geen BOL gevonden.",
    group: "Operatie",
  },
  airwaybill: {
    entity_type: "airwaybill",
    label: "Air Waybills",
    icon: Plane,
    placeholder: "Zoek op AWB-nummer…",
    emptyLabel: "Geen AWB gevonden.",
    group: "Operatie",
  },
  warehouse_item: {
    entity_type: "warehouse_item",
    label: "Warehouse Items",
    icon: Warehouse,
    placeholder: "Zoek op W-nummer of locatie…",
    emptyLabel: "Geen warehouse-item gevonden.",
    group: "Warehouse",
  },
  container: {
    entity_type: "container",
    label: "Containers",
    icon: ContainerIcon,
    placeholder: "Zoek op containernummer…",
    emptyLabel: "Geen container gevonden.",
    group: "Warehouse",
  },
  employee: {
    entity_type: "employee",
    label: "Medewerkers",
    icon: UserCog,
    placeholder: "Zoek op naam of e-mail…",
    emptyLabel: "Geen medewerker gevonden.",
    group: "Beheer",
  },
  ticket: {
    entity_type: "ticket",
    label: "Tickets",
    icon: Ticket,
    placeholder: "Zoek op tickenummer of onderwerp…",
    emptyLabel: "Geen ticket gevonden.",
    group: "Support",
  },
  asset: {
    entity_type: "asset",
    label: "Assets",
    icon: Boxes,
    placeholder: "Zoek op asset-tag of naam…",
    emptyLabel: "Geen asset gevonden.",
    group: "Assets",
  },
  contact: {
    entity_type: "contact",
    label: "Contactpersonen",
    icon: PhoneIncoming,
    placeholder: "Zoek op naam of e-mail…",
    emptyLabel: "Geen contactpersoon gevonden.",
    group: "Klanten",
  },
};

/**
 * Runtime-registratie voor toekomstige modules — laat een module zijn eigen
 * entity-config toevoegen zonder deze file te wijzigen.
 */
export function registerEntityConfig(config) {
  if (!config?.entity_type) {
    throw new Error("registerEntityConfig: entity_type is required");
  }
  ENTITY_CONFIGS[config.entity_type] = { ...config };
}

/**
 * Lookup helper — retourneert config of een sensible fallback (Users icon,
 * generieke placeholder) zodat de picker nooit crasht bij onbekende types.
 */
export function getEntityConfig(entityType) {
  return ENTITY_CONFIGS[entityType] || {
    entity_type: entityType,
    label: entityType,
    icon: Users,
    placeholder: "Zoeken…",
    emptyLabel: "Geen resultaten.",
    group: "Overig",
  };
}
